import {
	ChangeDetectionStrategy,
	Component,
	computed,
	inject,
	input,
} from '@angular/core';

import { EInvoiceReceivedGridItem, PurchaseGridItem } from '@skylab-angular-shared/business';
import { EnumTranslator, EnumTranslatorBase } from '@skylab-angular-shared/core';
import { TooltipDirective, IconComponent } from '@skylab-angular-shared/ui';
import { SaleGridItem } from '@skylab-shared/business';
import { DdkState, EppState } from '@skylab-shared/dataModel';
import { isEInvoicesGridItem } from '../../dialogs/purchase-service-payment/purchase-service-payment-dialog.component';
import { CommonColors } from '@inscomponents/core';
import { IconName } from '@skylab-assets/icons';

type IconConfig = { name: IconName; color: CommonColors };

@Component({
	selector: 'skl-accountancy-cell',
	template: `
		<div
			class="cell-icon-wrapper"
			[tooltip]="tooltip()"
		>
			<skl-icon
				[name]="iconConfig().name"
				[color]="iconConfig().color"
			/>
		</div>
	`,
	imports: [TooltipDirective, IconComponent],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccountancyCellComponent {
	readonly #enumTranslator = inject(EnumTranslator) as unknown as EnumTranslatorBase;

	readonly entity = input.required<PurchaseGridItem | SaleGridItem | EInvoiceReceivedGridItem>();

	readonly isNotApplicable = computed(() => {
		const ent = this.entity();
		return isEInvoicesGridItem(ent) && !ent.documentNumber;
	});

	readonly iconConfig = computed((): IconConfig => {
		if (this.isNotApplicable()) {
			return { name: 'circle-empty', color: 'secondary' };
		}

		const ent = this.entity();
		const isProcessed = ent.ddkState === DdkState.Processed || ent.eppState === EppState.Processed;

		return {
			name: 'calc',
			color: isProcessed ? 'success' : 'secondary',
		};
	});

	readonly tooltip = computed(() => {
		if (this.isNotApplicable()) {
			return this.#enumTranslator.getLabeledEnumValue(DdkState, DdkState.NotApplicable)?.label ?? '';
		}

		const ent = this.entity();
		const isProcessed = ent.ddkState === DdkState.Processed || ent.eppState === EppState.Processed;
		const state = isProcessed ? DdkState.Processed : DdkState.NotProcessed;

		return this.#enumTranslator.getLabeledEnumValue(DdkState, state)?.label ?? '';
	});
}
